import { apiClient } from './client';
import type { components, paths } from './generated/schema';
import { unwrapData } from './utils';

export type PlatformListResponse = components['schemas']['PlatformListResponse'];
export type PlatformAccountResponse = components['schemas']['PlatformAccount'];
export type PlatformListQuery = {
  userId?: string;
  username?: string;
};

type ConnectPlatformBody =
  paths['/platforms/connect']['post']['requestBody']['content']['application/json'];

type ConnectPlatformPlatforms = ConnectPlatformBody extends { platform: infer P } ? P : never;

export type ConnectPlatformInput = Omit<ConnectPlatformBody, 'platform'> & {
  platform: ConnectPlatformPlatforms;
};

type ConnectPlatformArgs = {
  payload: ConnectPlatformInput;
  returnUrl?: string;
};

export const listPlatforms = async (params?: PlatformListQuery): Promise<PlatformListResponse> => {
  const query =
    params && (params.userId?.trim() || params.username?.trim())
      ? {
          ...(params.userId?.trim() ? { userId: params.userId.trim() } : {}),
          ...(params.username?.trim() ? { username: params.username.trim() } : {}),
        }
      : undefined;

  const result = await apiClient.GET('/platforms', query ? { params: { query } } : undefined);

  return unwrapData<PlatformListResponse>(result);
};

export const connectPlatform = async ({ payload, returnUrl }: ConnectPlatformArgs) => {
  const result = await apiClient.POST('/platforms/connect', {
    body: payload as ConnectPlatformBody,
    params: returnUrl
      ? {
          query: {
            returnUrl,
          },
        }
      : undefined,
  });

  return unwrapData<components['schemas']['PlatformConnectResponse']>(result);
};

export const refreshPlatformToken = async (accountId: string) => {
  const result = await apiClient.POST('/platforms/{accountId}/refresh', {
    params: { path: { accountId } },
  });

  return unwrapData<components['schemas']['PlatformRefreshResponse']>(result);
};

export const getPlatformAccount = async (accountId: string): Promise<PlatformAccountResponse> => {
  const result = await apiClient.GET('/platforms/{accountId}', {
    params: { path: { accountId } },
  });

  return unwrapData<PlatformAccountResponse>(result);
};

export const disconnectPlatform = async (accountId: string) => {
  const result = await apiClient.DELETE('/platforms/{accountId}', {
    params: { path: { accountId } },
  });

  return unwrapData<components['schemas']['PlatformDisconnectResponse']>(result);
};
